import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  ArchiveRestore,
  BarChart3,
  Copy,
  Download,
  Eye,
  EyeOff,
  Globe,
  MoreHorizontal,
  Settings2,
  Sparkles,
  Tag,
  Trash2,
} from 'lucide-react';
import { isLyrraOne } from '@/lib/platform';

export type FlowRowActionsProps = {
  isShell: boolean;
  isEditorShared: boolean;
  enterpriseLinked: boolean;
  status: string;
  hidePublish?: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onExportJson?: () => void;
  onTogglePublish: () => void;
  onArchive: () => void;
  onRestoreArchive: () => void;
  onDelete: () => void;
  onOpenOffer?: () => void;
  onGenerateFunnelAi?: () => void;
  onManageCategories: () => void;
  onOpenProperties: () => void;
  onOpenDashboard?: () => void;
};

type MenuItem = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  className?: string;
  dividerBefore?: boolean;
};

export function FlowRowActions({
  isShell,
  isEditorShared,
  enterpriseLinked,
  status,
  hidePublish = false,
  onEdit,
  onDuplicate,
  onExportJson,
  onTogglePublish,
  onArchive,
  onRestoreArchive,
  onDelete,
  onOpenOffer,
  onGenerateFunnelAi,
  onManageCategories,
  onOpenProperties,
  onOpenDashboard,
}: FlowRowActionsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  const updateMenuPos = () => {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPos();
    const onScrollOrResize = () => updateMenuPos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const node = e.target as Node;
      if (buttonRef.current?.contains(node)) return;
      if (menuRef.current?.contains(node)) return;
      setOpen(false);
      setMenuPos(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    setMenuPos(null);
  };

  const run = (fn: () => void) => {
    fn();
    closeMenu();
  };

  const items: MenuItem[] = [];

  if (isShell) {
    items.push({
      key: 'missions',
      label: t('eduflow.list.tooltip.openMissions'),
      icon: Eye,
      onClick: () => run(onEdit),
    });
  } else if (isEditorShared) {
    items.push({
      key: 'play',
      label: t('eduflow.list.tooltip.playFlow', 'Visualiser la formation'),
      icon: Eye,
      onClick: () => run(onEdit),
    });
  } else {
    items.push({
      key: 'edit',
      label: t('eduflow.list.tooltip.edit'),
      icon: Eye,
      onClick: () => run(onEdit),
    });
    items.push({
      key: 'properties',
      label: t('eduflow.list.flowProperties', 'Titre, description et couverture'),
      icon: Settings2,
      onClick: () => run(onOpenProperties),
    });
    if (onOpenDashboard) {
      items.push({
        key: 'dashboard',
        label: t('contentDashboard.open', 'Tableau de bord'),
        icon: BarChart3,
        onClick: () => run(onOpenDashboard),
      });
    }
    if (isLyrraOne() && status === 'published' && onOpenOffer) {
      items.push({
        key: 'offer',
        label: t('lyrraone.store.offerTitle', 'Paramètres de vente'),
        icon: Tag,
        onClick: () => run(onOpenOffer),
      });
    }
    if (isLyrraOne() && status === 'published' && onGenerateFunnelAi) {
      items.push({
        key: 'funnel-ai',
        label: t('lyrraone.funnelAi.actionTooltip', 'Générer un funnel de vente par IA'),
        icon: Sparkles,
        onClick: () => run(onGenerateFunnelAi),
      });
    }
    items.push({
      key: 'duplicate',
      label: t('btn.duplicate'),
      icon: Copy,
      onClick: () => run(onDuplicate),
    });
    if (onExportJson) {
      items.push({
        key: 'export-json',
        label: t('eduflow.list.exportJsonLight', 'Exporter en JSON (sans médias)'),
        icon: Download,
        onClick: () => run(onExportJson),
      });
    }
    if (status === 'archived') {
      items.push({
        key: 'restore',
        label: t('eduflow.list.restoreFromArchive', 'Restaurer depuis les archives (brouillon)'),
        icon: ArchiveRestore,
        onClick: () => run(onRestoreArchive),
      });
    } else {
      if (!hidePublish) {
        items.push({
          key: 'publish',
          label: status === 'published' ? t('btn.unpublish') : t('btn.publish'),
          icon: status === 'published' ? EyeOff : Globe,
          onClick: () => run(onTogglePublish),
          className: 'text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-950/30',
        });
      }
      items.push({
        key: 'archive',
        label: t('eduflow.list.archiveFormation', 'Archiver la formation'),
        icon: Archive,
        onClick: () => run(onArchive),
      });
    }
    items.push({
      key: 'categories',
      label: t('eduflow.list.manageCategories', 'Catégories'),
      icon: Tag,
      onClick: () => run(onManageCategories),
    });
    if (!enterpriseLinked) {
      items.push({
        key: 'delete',
        label: t('btn.delete'),
        icon: Trash2,
        onClick: () => run(onDelete),
        className: 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30',
        dividerBefore: true,
      });
    }
  }

  const menu = open && menuPos && createPortal(
    <ul
      ref={menuRef}
      style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
      className="z-[200] min-w-[200px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
      role="menu"
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li key={item.key} role="none" className={item.dividerBefore ? 'border-t border-gray-100 dark:border-gray-800' : undefined}>
            <button
              type="button"
              onClick={item.onClick}
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800 ${item.className ?? ''}`}
              role="menuitem"
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="text-left">{item.label}</span>
            </button>
          </li>
        );
      })}
    </ul>,
    document.body,
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((wasOpen) => {
            if (wasOpen) {
              setMenuPos(null);
              return false;
            }
            const el = buttonRef.current;
            if (el) {
              const r = el.getBoundingClientRect();
              setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
            }
            return true;
          });
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        aria-label={t('common.actions', 'Actions')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {menu}
    </>
  );
}
